import { format, isValid, parseISO } from "date-fns";

export const CASE_DATE_DISPLAY_FORMAT = "dd MMM yyyy";

/** Format a complete calendar date without a timezone shift or invented missing parts. */
export function calendarDateLabel(value: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const date = parseISO(value);
  return isValid(date) ? format(date, CASE_DATE_DISPLAY_FORMAT) : undefined;
}

/**
 * A zoned instant, written the way the reviewer's own clock reads it.
 *
 * <p>The review document publishes true UTC instants with a `Z` (D120a). Rendering one unformatted
 * puts `2026-09-11T07:21:52Z` on screen, so a reviewer in IST reads 07:21 for a note they made at
 * 12:51 — and it looked right before only because the value was a local wall-clock wearing a `Z`,
 * two faults cancelling. Formatting is what makes the corrected wire readable.
 *
 * <p>Keyed on the schema's declared `date-time` format rather than on any field, so every zoned
 * instant the catalog gains later is rendered this way without anyone remembering to ask.
 *
 * <p>A sibling of the header's date formatters in intent, and a leaf module in fact: the read-mode
 * display helper is in `shared/`, the header's formatters are in `shell/`, and shared must not
 * import shell.
 */
export function localDateTimeLabel(value: string): string | undefined {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return undefined;
  // Explicit parts rather than toLocaleString's default, which varies by locale between a comma
  // and a slash and between 2 and 4 digit years; the table reads as a column, so it stays stable.
  const date = instant.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  const time = instant.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
}
